'use client';

import { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { H4 } from '../styled/typography';
import {
  Comment,
  CommentPicture,
  CommentPostData,
  CommentResponse,
} from '../../models/comment';
import { http } from '../../lib/api/http';
import { City } from '../../models';
import { V3 } from '../../lib/api/urls';
import { CommentItem } from './CommentItem';
import { PictureModal } from '../picture/PictureModal';
import { ImageProp } from '../../models/image';
import { useModal } from '../../providers/modal';
import { CaptchaModal } from './CaptchaModal';
import { CommentsModalError } from './CommentsModalError';
import { useAuthorization } from '../../providers/authorization';
import { CommentFormModal } from './CommentFormModal';
import { CommentsModalSuccess } from './CommentsModalSuccess';
import { setLocalStorageItem } from '../../lib/api/browserStorage';

const modalOptions = {
  width: 490,
  background: 'unset',
  boxShadow: '0 15px 40px 0 rgba(0, 0, 0, 0.5)',
};

export function CommentsBlock({
  contentType,
  eventId,
  currentCity = null,
}: {
  contentType: string;
  eventId: number;
  currentCity: City | null;
}) {
  const theme = useTheme();
  const modalState = useModal();
  const auth = useAuthorization();

  const [comments, setComments] = useState<Comment[]>([]);
  const [count, setCount] = useState(0);
  const [images, setImages] = useState<ImageProp[]>([]);

  const localStorageDescriptionKey = `saved_comment_${eventId}`;

  const [imageModal, setImageModal] = useState<{
    slide: number | null;
    visible: boolean;
  }>({ slide: null, visible: false });

  const [captchaModal, setCaptchaModal] = useState(false);
  const [errorModal, setErrorModal] = useState({
    visible: false,
    text: '',
  });
  const [successModal, setSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<{
    title: string;
    description: string;
  }>({ title: '', description: '' });

  const loadMore = async () => {
    const newComments = await http.get(
      V3.comments,
      {
        content_type__model: contentType.split('.')[1],
        limit: 3,
        offset: comments.length,
        object_id: eventId,
        with_inactive: 'true',
        ...(currentCity && { city: currentCity.id }),
      },
      null,
      {
        contentType: 'json',
        withTokenRequest: true,
      }
    );
    setComments((prev) => [...prev, ...newComments.results]);
  };

  const sendCommentPost = async (data: {
    name?: string;
    email?: string;
    phone_number?: string;
    description: string;
    rating: number | null;
    loadedImages: CommentPicture[];
    captcha?: string;
    reply: Comment | null;
    idempotency_key: string;
  }) => {
    const commentData: CommentPostData = {
      content_type: contentType,
      description: data.description,
      status: 'new',
      object_id: eventId,
      idempotency_key: data.idempotency_key,
      parent: data.reply?.id ?? '',
    };

    if (data.captcha) {
      commentData.token = data.captcha;
    }

    if (data.name) {
      commentData.name = data.name;
    }

    if (data.email) {
      commentData.email = data.email;
    }

    if (data.idempotency_key) {
      commentData.idempotency_key = data.idempotency_key;
    }

    if (data.phone_number) {
      commentData.phone_number = data.phone_number;
    }

    if (data.loadedImages?.length) {
      data.loadedImages.forEach(({ file }, ind) => {
        commentData[`file_${ind}`] = file;
      });
    }

    if (data.rating) commentData.rating = data.rating;
    await http
      .post(
        V3.comments,
        commentData,
        {},
        { contentType: 'formData', withTokenRequest: true }
      )
      .then((res: { status: number; data: CommentResponse }) => {
        if (res.status === 200 || res.status === 201) {
          setSuccessModalData(res.data.popup);
          setSuccessModal(true);
          localStorage.removeItem(localStorageDescriptionKey);
          if (!res.data.parent) {
            const updateComments = [res.data, ...comments];
            setComments(updateComments);
            setCount((oldCount) => oldCount + 1);
          } else {
            const updateComments = comments.map((comment) =>
              Number(comment.id) === Number(res.data.parent)
                ? { ...comment, children_count: comment.children_count + 1 }
                : comment
            );
            setComments(updateComments);
          }
        } else {
          if (
            Object.keys(res.data).includes('email') ||
            Object.keys(res.data).includes('phone_number')
          ) {
            setLocalStorageItem(
              localStorageDescriptionKey,
              JSON.stringify({
                description: commentData.description,
              })
            );
          }
          setErrorModal({
            visible: true,
            text: Object.values(res.data).join(' '),
          });
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setCaptchaModal(false);
      });
  };

  const sendComment = async (data: {
    name?: string;
    email?: string;
    phone_number?: string;
    description: string;
    rating: number | null;
    loadedImages: CommentPicture[];
    reply: Comment | null;
    idempotency_key: string;
  }) => {
    if (auth && auth.isReady) {
      if (!auth.isAuthenticated) {
        setCaptchaModal(true);
        const getCaptchaValue = async () => {
          const input = document.querySelector(
            '[name=smart-token]'
          ) as HTMLInputElement;
          if (input && input.value) {
            await sendCommentPost({
              ...data,
              captcha: input.value,
            });
          } else {
            setTimeout(getCaptchaValue, 800);
          }
        };

        setTimeout(getCaptchaValue, 800);
      } else {
        await sendCommentPost(data);
      }
    } else {
      setTimeout(() => sendComment(data), 800);
    }
  };

  const getParent = (id: number) => comments.find((c) => c.id === id) || null;

  useEffect(() => {
    const fetch = async () => {
      const reqParams = {
        content_type__model: contentType.split('.')[1],
        limit: 3,
        object_id: eventId,
        with_inactive: 'true',
        ...(currentCity && { city: currentCity.id }),
      };
      const newComments = await http.get(V3.comments, reqParams, null, {
        contentType: 'json',
        withTokenRequest: true,
      });
      setComments(newComments.results);
      setCount(newComments.count);
    };
    if (auth.isReady) {
      fetch();
    }
  }, [eventId, contentType, auth]);

  useEffect(() => {
    if (captchaModal) {
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        content: <CaptchaModal setModalState={modalState.setModalState} />,
      }));
    } else if (successModal) {
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        options: modalOptions,
        content: (
          <CommentsModalSuccess
            modal={{
              title: successModalData.title,
              description: successModalData.description,
            }}
            setModalState={modalState.setModalState}
          />
        ),
      }));
    } else if (errorModal.visible) {
      modalState?.setModalState((old) => ({
        ...old,
        show: true,
        options: modalOptions,
        content: (
          <CommentsModalError
            modal={{
              title: 'Ошибка',
              description: errorModal.text,
            }}
            close={() => setErrorModal({ visible: false, text: '' })}
            setModalState={modalState.setModalState}
          />
        ),
      }));
    }
  }, [successModal, captchaModal, errorModal.visible]);

  const onOpenCommentFormModal = (reply: Comment | null) => {
    if (modalState) {
      setSuccessModal(false);
      setErrorModal({ visible: false, text: '' });
      setCaptchaModal(false);
      modalState.setModalState({
        content: (
          <CommentFormModal
            localStorageDescriptionKey={localStorageDescriptionKey}
            reply={reply}
            sendComment={sendComment}
            setImagesSlider={setImageModal}
            setImages={setImages}
            setModalState={modalState.setModalState}
          />
        ),
        show: true,
        options: {
          width: 620,
        },
      });
    }
  };

  return (
    <div
      className='comments-block'
      css={{
        scrollMargin: 50,
      }}
    >
      <H4
        css={{
          fontSize: 13,
          fontWeight: 'bold',
          letterSpacing: 1,
          color: theme.colors.primary,
          textTransform: 'uppercase',
        }}
      >
        Отзывы
      </H4>
      <button
        css={{
          background: theme.colors.primary,
          backgroundImage: 'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
          color: '#fff',
          fontSize: 16,
          padding: '9px 26px',
          borderRadius: 40,
          marginTop: 24,
          marginBottom: 24,
          transition: '.3s',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0px 0px 10px 0px #de2839',
          },
        }}
        type='button'
        onClick={() => {
          onOpenCommentFormModal(null);
        }}
      >
        Написать отзыв
      </button>
      <div>
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            getParent={getParent}
            setImagesSlider={setImageModal}
            setImages={setImages}
            onOpenCommentFormModal={onOpenCommentFormModal}
          />
        ))}
      </div>

      {count !== comments.length && (
        <button
          css={{
            background: '#dfdfdf',
            borderRadius: 30,
            width: '100%',
            padding: 8,
            fontSize: 16,
            transition: '.15s',
            border: '2px solid #dfdfdf',
            cursor: 'pointer',
            '&:hover': {
              background: 'transparent',
            },
          }}
          onClick={loadMore}
          type='button'
        >
          Показать ещё
        </button>
      )}
      {imageModal.visible && (
        <PictureModal
          images={images}
          imageModal={imageModal}
          scrollY={window.scrollY}
          setVisible={setImageModal}
        />
      )}
    </div>
  );
}
